/**
 * mandala — combines a curl-noise warp field with a 8-segment kaleidoscope
 * composite and bloom. The curl noise produces organic motion; the
 * kaleidoscope folds it into radial mandala-like symmetry; bloom gives the
 * fine ridges a glow-y halo.
 *
 * The adoption cooldown is long (~2.5s) so the mandala has time to settle
 * before re-folding into a new field shape.
 */

import { MirrorMode, WarpKind, type VisualizerPreset } from '../presetTypes';

export const mandalaPreset: VisualizerPreset = {
  id: 'mandala',
  name: 'Mandala',
  seed: {
    kind: 'waveform',
    params: {
      baseY: 0.0,
      gain: 0.4,
      halfThick: 0.011,
      intensity: 0.55,
      color: [1.0, 0.9, 0.65], // warm gold
      edgeSharp: 9.0,
    },
  },
  warp: {
    kind: WarpKind.Field,
    params: {
      baseZoom: 0.005,
      bassZoomGain: 0.022,
      rotation: 0,
      bassRotationGain: 0,
      ripple: 0,
      rippleFreq: 0,
      decay: 0.972,
      chroma: 0.0024,
      hueShift: 0.008,
      beatPunch: 0.04,
      centerDriftAmp: 0.02,
      centerDriftRate: 0.07,
    },
  },
  warpField: {
    generator: 'curl',
    seed: 4.7,
    strength: 0.011,
    centerX: 0.5,
    centerY: 0.5,
    param0: 5.0, // tighter noise = more detailed mandala
    param1: 0,
    param2: 0,
    adoption: {
      beatThreshold: 0.7,
      cooldownMs: 2500,
      crossfadeMs: 800,
      regenerateOnAdopt: true,
    },
  },
  modulation: [
    {
      target: 'warp.baseZoom',
      source: 'bass',
      transform: 'smoothPulse',
      amount: 0.014,
      smoothingMs: 130,
    },
    {
      target: 'seed.intensity',
      source: 'rms',
      transform: 'linear',
      amount: 0.35,
      smoothingMs: 80,
    },
  ],
  transition: { durationMs: 1100 },
  composite: {
    mirror: MirrorMode.Kaleidoscope,
    kaleidoscopeSegments: 8,
    contrast: 1.05,
  },
  present: {
    bloomEnabled: true,
    bloomThreshold: 0.6,
    bloomKnee: 0.22,
    bloomSpread: 1.3,
    bloomIntensity: 0.8,
    vignette: 0.25,
    grain: 0.01,
  },
};
